import React, { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Textarea } from '../../../components/ui/textarea';
import { CustomerFormData } from '../types';

interface CustomerFormProps {
  customer?: CustomerFormData;
  onSubmit: (data: CustomerFormData) => void;
  onCancel: () => void;
}

interface FormErrors {
  name?: string;
  document?: string;
  email?: string;
  phone?: string;
  address?: string;
  birthDate?: string;
}

export const CustomerForm: React.FC<CustomerFormProps> = ({
  customer,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState<CustomerFormData>({
    name: '',
    document: '',
    documentType: 'DNI',
    email: '',
    phone: '',
    address: '',
    birthDate: '',
    tags: [],
  });
  
  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (customer) {
      setFormData(customer);
    }
  }, [customer]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    }

    if (!formData.document.trim()) {
      newErrors.document = 'El documento es requerido';
    } else if (formData.documentType === 'DNI' && formData.document.length !== 8) {
      newErrors.document = 'El DNI debe tener 8 dígitos';
    } else if (formData.documentType === 'RUC' && formData.document.length !== 11) {
      newErrors.document = 'El RUC debe tener 11 dígitos';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Ingrese un email válido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleInputChange = (field: keyof CustomerFormData, value: string) => {
    setFormData({ ...formData, [field]: value });
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors({ ...errors, [field]: undefined });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Nombre */}
        <div className="md:col-span-2">
          <Label htmlFor="name">Nombre completo</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="Ingrese el nombre completo"
          />
          {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
        </div>

        {/* Tipo de documento */}
        <div>
          <Label>Tipo de documento</Label>
          <Select 
            value={formData.documentType}
            onValueChange={(value: 'DNI' | 'RUC') => handleInputChange('documentType', value)}
          >
            <SelectTrigger aria-label="Tipo de documento">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="DNI">DNI</SelectItem>
              <SelectItem value="RUC">RUC</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Número de documento */}
        <div>
          <Label htmlFor="document">Número de documento</Label>
          <Input
            id="document"
            value={formData.document}
            onChange={(e) => handleInputChange('document', e.target.value)}
            placeholder={formData.documentType === 'DNI' ? 'Ej: 12345678' : 'Ej: 20123456789'}
          />
          {errors.document && <p className="text-red-500 text-sm mt-1">{errors.document}</p>}
        </div>

        {/* Email */}
        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            placeholder="Ej: cliente@ejemplo.com"
          />
          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
        </div>

        {/* Teléfono */}
        <div>
          <Label htmlFor="phone">Teléfono</Label>
          <Input
            id="phone"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            placeholder="Ej: 987654321"
          />
          {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
        </div>

        {/* Dirección */}
        <div className="md:col-span-2">
          <Label htmlFor="address">Dirección</Label>
          <Textarea
            id="address"
            value={formData.address}
            onChange={(e) => handleInputChange('address', e.target.value)}
            placeholder="Ej: Av. Principal 123, Lima"
            rows={2}
          />
          {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
        </div>

        {/* Fecha de nacimiento */}
        <div>
          <Label htmlFor="birthDate">Fecha de nacimiento</Label>
          <Input
            id="birthDate"
            type="date"
            value={formData.birthDate}
            onChange={(e) => handleInputChange('birthDate', e.target.value)}
          />
          {errors.birthDate && <p className="text-red-500 text-sm mt-1">{errors.birthDate}</p>}
        </div>
      </div>

      {/* Botones */}
      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" className="bg-macartur-primary hover:bg-macartur-accent1">
          Guardar
        </Button>
      </div>
    </form>
  );
};